import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import type { SegmentQuoteItem } from '../api/client';

// ─────────────────────────────────────────────────────────────────────────────
// Market-Research Insights section on the segment profile page. Renders the
// quotes that were extracted at segment-save time by the hackathon
// quote-extraction pipeline (see useSaveSegmentPages.ts).
//
// Data source: segment metadata.quotes = { subject, items: SegmentQuoteItem[] }
// where SegmentQuoteItem comes from FindRelevantQuotesQueryEngine. Each item
// carries transcriptId + startTs/endTs so this widget can link directly to
// the source transcript in brand-insights.
// ─────────────────────────────────────────────────────────────────────────────

export interface SegmentQuotesData {
  subject?: string;
  items?: SegmentQuoteItem[];
}

interface Props {
  quotes: SegmentQuotesData | null | undefined;
  segmentName?: string;
}

export default function SegmentMarketResearchInsights({
  quotes,
  segmentName: _segmentName,
}: Props): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);
  const items = quotes?.items ?? [];

  if (!quotes || items.length === 0) return null;

  return (
    <Root>
      <Header onClick={() => setExpanded(!expanded)}>
        <HeaderTitle>
          <span>Market Research Insights</span>
        </HeaderTitle>
        <ExpandIcon $expanded={expanded}>
          <ExpandMoreIcon />
        </ExpandIcon>
      </Header>
      {expanded && (
        <Content>
          <QuoteGrid>
            {items.map((q, idx) => (
              <QuoteCard key={`${q.transcriptId}-${q.startTs}-${idx}`} quote={q} />
            ))}
          </QuoteGrid>
        </Content>
      )}
    </Root>
  );
}

// ─── Single quote card ───────────────────────────────────────────────────────

function QuoteCard({ quote }: { quote: SegmentQuoteItem }): React.JSX.Element {
  const link = buildTranscriptLink(quote);
  const timestampLabel = `${formatTime(quote.startTs)} – ${formatTime(quote.endTs)}`;

  return (
    <Card>
      <QuoteIconWrap>
        <FormatQuoteIcon fontSize="small" />
      </QuoteIconWrap>
      <QuoteBody>
        <QuoteText>&ldquo;{quote.value}&rdquo;</QuoteText>
        <QuoteFooter>
          <TranscriptLink
            to={link}
            title={`Open transcript #${quote.transcriptId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Transcript #{quote.transcriptId}
            <LinkChevron fontSize="inherit" />
          </TranscriptLink>
          <Timestamp>{timestampLabel}</Timestamp>
          {quote.starredQuoteId != null && <StarBadge>★ Starred</StarBadge>}
        </QuoteFooter>
      </QuoteBody>
    </Card>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Deep-link into the workspace transcript viewer. The real route is
// /workspaces/transcripts/:id?s=<start seconds> (see
// packages/chat-components/src/utils/navigation.ts → getLocationFor.workspaces.transcript).
// We start the playhead at the quote's startTs so the viewer opens at the
// matching moment.
function buildTranscriptLink(q: SegmentQuoteItem): string {
  const qs = new URLSearchParams({ s: String(Math.floor(q.startTs)) });
  return `/workspaces/transcripts/${q.transcriptId}?${qs.toString()}`;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 16px;
`;

const Header = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 20px;
  font-weight: 700;
  color: #323232;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 20px;
`;

const QuoteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  display: flex;
  gap: 10px;
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-left: 3px solid #28aae1;
  border-radius: 6px;
  padding: 12px 14px;
`;

const QuoteIconWrap = styled.div`
  color: #28aae1;
  opacity: 0.7;
  flex-shrink: 0;
`;

const QuoteBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const QuoteText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #323232;
  font-style: italic;
`;

const QuoteFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const TranscriptLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  color: #1271c4;
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const LinkChevron = styled(ChevronRightIcon)`
  color: #1271c4;
  font-size: 14px;
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: #626262;
  font-variant-numeric: tabular-nums;
`;

const StarBadge = styled.span`
  font-size: 10px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 10px;
  padding: 1px 6px;
  font-weight: 600;
`;
